import React from "react";

import { Box, List, ListItem, ListItemProps, Typography } from "@material-ui/core";
import clsx from "clsx";
import * as R from "ramda";

import OrderItem from "api/types/OrderItem";
import Price from "components/Price";
import makeStyles from "hooks/makeStyles";
import useLocalize from "hooks/useLocalize";

// NOTE: workaround for weird mui type error
const MyListItem = ListItem as React.FunctionComponent<ListItemProps>;

interface Props extends ListItemProps {
	item: Pick<
		OrderItem,
		"menuItem" | "quantity" | "unitPriceCents" | "extendedPriceCents" | "selectedMenuItemOptionGroups"
	>;
	variant: "light" | "dark";
}

const fontSize = "14px";
const lineHeight = "16px";

const useStyles = makeStyles.withProps<Props>(theme => ({
	root: {
		margin: 0,
		padding: 0,
		display: "flex",
		flexDirection: "column",
		alignItems: "stretch",
		color: ({ variant }) => (variant === "light" ? "000" : "#fff"),
		borderTop: ({ variant }) => (variant === "light" ? "1px solid #bdbdbd" : "1px solid #fff")
	},
	menuItem: {
		margin: `${theme.spacing(2)}px ${theme.spacing(1)}px 0 ${theme.spacing(1)}px`,
		display: "flex",
		flexDirection: "row",
		alignItems: "baseline",
		justifyContent: "space-between"
	},
	itemName: {
		fontSize,
		lineHeight,
		fontWeight: 500,
		minWidth: 0,
		whiteSpace: "normal",
		textTransform: "uppercase"
	},
	itemPrice: {
		fontSize,
		lineHeight
	},
	options: {
		margin: `0 ${theme.spacing(1)}px 0 ${theme.spacing(3)}px`,
		padding: 0,
		listStyle: "none"
	},
	option: {
		margin: `${theme.spacing(1)}px 0 0 0`,
		padding: 0,
		display: "flex",
		flexDirection: "row",
		alignItems: "baseline",
		justifyContent: "space-between",
		color: theme.palette.text.secondary
	},
	optionName: {
		fontSize,
		lineHeight,
		minWidth: 0,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		textTransform: "uppercase"
	},
	optionPrice: {
		fontSize,
		lineHeight
	},
	total: {
		margin: `${theme.spacing(1)}px ${theme.spacing(1)}px 0 ${theme.spacing(1)}px`,
		display: "flex",
		flexDirection: "row",
		alignItems: "baseline",
		justifyContent: "flex-end"
	},
	itemTotal: {
		fontSize,
		lineHeight,
		fontWeight: 500
	}
}));

const OrderItemListItem = (props: Props) => {
	const { className, item, variant, ...otherProps } = props;

	const { localize } = useLocalize();

	const classes = useStyles(props);

	const { menuItem, selectedMenuItemOptionGroups } = item;

	const options = R.flatten(selectedMenuItemOptionGroups.map(x => x.selectedMenuItemOptions));

	return (
		<MyListItem className={clsx(classes.root, className)} {...otherProps}>
			<Box className={classes.menuItem}>
				<Typography className={classes.itemName}>{localize(menuItem.localeNames, menuItem.name)}</Typography>
				{/*<Typography className={classes.itemCode}>{menuItem.itemCode}</Typography>*/}
				<Price className={classes.itemPrice} priceCents={item.extendedPriceCents} />
			</Box>

			{options.length === 0 ? null : (
				<List className={classes.options}>
					{options.map(option => (
						<ListItem key={option.menuItemOptionId} className={classes.option}>
							<Box className={classes.optionName}>&bull; {localize(option.localeNames, option.name)}</Box>
							<Box>
								<Price
									className={classes.optionPrice}
									priceCents={option.priceCents}
									displayPrice={option.displayPrice}
								/>
							</Box>
						</ListItem>
					))}
				</List>
			)}

			<Box className={classes.total}>
				<Price className={classes.itemTotal} priceCents={item.extendedPriceCents} />
			</Box>
		</MyListItem>
	);
};

export default OrderItemListItem;
